import { jsx as _jsx } from "react/jsx-runtime";
import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, createHttpLink, from, gql, } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from '../hooks';
import { resetAnalytics } from '../utilities/analytics';
export const KEEP_ALIVE = gql `
  mutation KEEP_ALIVE {
    keepAlive
  }
`;
/**
 * Provides a GraphQL client using Apollo for making GraphQL requests.
 * @param children - The child components to be wrapped with the ApolloProvider.
 * @param url - The URL of the GraphQL server. Defaults to '/graphql'.
 * @param headers - The headers to be sent with each request.
 */
export const GqlProvider = ({ children, url = '/graphql', headers }) => {
    const navigate = useNavigate();
    // use nonce to initiate keep alive queries
    const [nonce, setNonce] = useState('');
    // Debounce the nonce value to let it settle down before using it
    const debouncedNonce = useDebounce(nonce, 5000);
    useEffect(() => {
        // When the debounced nonce changes, initiate a keep alive query
        (async () => {
            if (debouncedNonce) {
                try {
                    await client.current.mutate({ mutation: KEEP_ALIVE });
                }
                catch { } // swallow keep alive errors
            }
        })();
    }, [debouncedNonce]);
    const client = useRef(new ApolloClient({
        defaultOptions: {
            watchQuery: {
                // Always refetch from server only for Admin pages
                fetchPolicy: 'cache-and-network',
            },
        },
        cache: new InMemoryCache(),
        link: from([
            new ApolloLink((operation, forward) => {
                operation.setContext(({ headers: existingHeaders = {} }) => ({
                    headers: {
                        ...(existingHeaders || {}),
                        ...(headers || {}),
                    },
                }));
                // Update the nonce value for every operation except 'KEEP_ALIVE'
                return forward(operation).map(data => {
                    if (operation.operationName !== 'KEEP_ALIVE') {
                        setNonce(Math.random().toString());
                    }
                    return data;
                });
            }),
            onError(({ graphQLErrors, networkError }) => {
                if (graphQLErrors) {
                    graphQLErrors.forEach(({ message, locations, path, extensions }) => {
                        if (message === '') {
                            // Probably a session timeout
                            resetAnalytics();
                            navigate('/?timeout=true');
                            return;
                        }
                        console.error('[GraphQL error]', { message, locations, path, extensions });
                    });
                }
                if (networkError) {
                    console.error('[Network error]', { data: networkError });
                }
            }),
            createHttpLink({ fetch, uri: url }),
        ]),
    }));
    return _jsx(ApolloProvider, { client: client.current, children: children });
};
